/**
 * Swaps the list for a form (replace) or stacks the form above/below (collapse).
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

/** `replace` hides the list; `collapse` stacks the form above/below. */
export type CollapseReplaceMode = 'replace' | 'collapse';

/** True in `replace` mode while the form is open (the list is visually hidden). */
export const CollapseReplaceHiddenContext = React.createContext(false);

/** Props for {@link CollapseReplaceWrapper}. */
interface CollapseReplaceWrapperProps {
  mode: CollapseReplaceMode;
  isOpen: boolean;
  children: React.ReactNode;
  formContent: React.ReactNode;
  collapsePosition?: 'top' | 'bottom';
}

/** Hides or stacks list content while a create/edit form is open. */
export const CollapseReplaceWrapper: React.FC<CollapseReplaceWrapperProps> = ({
  mode,
  isOpen,
  children,
  formContent,
  collapsePosition = 'top',
}) => {
  switch (mode) {
    case 'replace':
      return (
        <CollapseReplaceHiddenContext.Provider value={isOpen}>
          <div className={isOpen ? undefined : 'hidden'}>{formContent}</div>
          <div className={isOpen ? 'hidden' : undefined}>{children}</div>
        </CollapseReplaceHiddenContext.Provider>
      );

    case 'collapse':
      return (
        <div className="space-y-0">
          {collapsePosition === 'top' && (
            <div
              className={cn(
                'collapse-container',
                isOpen ? 'collapse-enter-active' : 'collapse-exit-active',
              )}
            >
              <div className={cn('collapse-content collapse-content-top', !isOpen && 'hidden')}>
                {formContent}
              </div>
            </div>
          )}

          <div
            className={cn(
              'transition-all duration-400',
              isOpen && collapsePosition === 'top' && 'animate-slide-down',
            )}
          >
            {children}
          </div>

          {collapsePosition === 'bottom' && (
            <div
              className={cn(
                'collapse-container',
                isOpen ? 'collapse-enter-active' : 'collapse-exit-active',
              )}
            >
              <div className={cn('collapse-content collapse-content-bottom', !isOpen && 'hidden')}>
                {formContent}
              </div>
            </div>
          )}
        </div>
      );

    default:
      return <>{children}</>;
  }
};
